import React, { useState, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ModernStickyHeader } from '../components/ModernStickyHeader';
import { ArrowLeft, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';

export function BlogPage() {
  const [selectedCategory, setSelectedCategory] = useState('All');

  // Fix scroll position to start at top on load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [articles, setArticles] = useState<any[]>([]);

  // Fetch articles from Supabase
  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const { data, error } = await supabase
          .from('articles')
          .select('*')
          .eq('published', true)
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Error fetching articles:', error);
          setArticles([]);
        } else {
          setArticles(data || []);
        }
      } catch (error) {
        console.error('Error fetching articles:', error);
        setArticles([]);
      }
    };

    fetchArticles();
  }, []);

  // Get unique categories
  const categories = useMemo(() => {
    const cats = ['All', ...new Set(articles.map(a => a.category))];
    return cats;
  }, [articles]);

  // Filter articles
  const filteredArticles = useMemo(() => {
    let filtered = articles;

    // Category filter
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(article => article.category === selectedCategory);
    }

    // Sort by most recent
    filtered = [...filtered].sort((a, b) => 
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );

    return filtered;
  }, [articles, selectedCategory]);

  // Navigation links for blog page
  const navLinks = [
    { label: 'Home', href: '/' },
    { label: 'Projects', href: '/projects' },
    { label: 'Blog', href: '/blog' },
    { label: 'Contact', href: '/#contact' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-warm-white to-light-gray dark:from-gray-900 dark:to-gray-800">
      {/* Fixed Header with proper z-index */}
      <ModernStickyHeader navLinks={navLinks} />
      
      {/* Background Elements - repositioned for blog identity */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-32 left-16 w-40 h-40 bg-blue-200/15 dark:bg-blue-800/15 rounded-full blur-xl animate-pulse" />
        <div className="absolute top-60 right-24 w-32 h-32 bg-indigo-200/15 dark:bg-indigo-800/15 rounded-full blur-xl animate-pulse" style={{ animationDelay: '1.5s' }} />
        <div className="absolute bottom-32 left-1/3 w-48 h-48 bg-purple-200/15 dark:bg-purple-800/15 rounded-full blur-xl animate-pulse" style={{ animationDelay: '3s' }} />
      </div>

      <div className="relative z-10 pt-18">
        {/* Hero Section - Enhanced for blog identity */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="pt-20 pb-16 px-4 sm:px-6 lg:px-8"
        >
          <div className="max-w-4xl mx-auto">
            {/* Back to Home Link */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="mb-12"
            >
              <Link
                to="/"
                className="group inline-flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-orange-600 dark:hover:text-orange-400 transition-colors duration-300"
              >
                <motion.div
                  whileHover={{ x: -2 }}
                  transition={{ duration: 0.2 }}
                >
                  <ArrowLeft className="w-4 h-4" />
                </motion.div>
                <span className="text-sm font-medium">Back to Home</span>
              </Link>
            </motion.div>

            <div className="text-center">
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="text-5xl md:text-6xl font-light text-gray-900 dark:text-white mb-6"
              >
                Latest Thinking
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-2xl mx-auto leading-relaxed"
              >
                Thoughts on web development, design, and technology
              </motion.p>
            </div>
          </div>
        </motion.section>

        {/* Simple Category Filter */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="px-4 sm:px-6 lg:px-8 mb-16"
        >
          <div className="max-w-4xl mx-auto">
            <div className="flex justify-center">
              <div className="flex gap-2 bg-white/30 dark:bg-gray-800/30 backdrop-blur-sm border border-gray-200/30 dark:border-gray-700/30 rounded-full p-2">
                {categories.map(category => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-6 py-2 text-sm font-medium rounded-full transition-all duration-300 ${
                      selectedCategory === category
                        ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-white/50 dark:hover:bg-gray-700/50'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </motion.section>

        {/* Articles List - Clean and Minimal */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="px-4 sm:px-6 lg:px-8 pb-24"
        >
          <div className="max-w-4xl mx-auto">
            <div className="space-y-16">
              {filteredArticles.map((article, index) => (
                <motion.article
                  key={article.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 * index }}
                  className="group cursor-pointer"
                >
                  <Link to={`/blog/${article.slug}`}>
                    <div className="space-y-4">
                      {/* Category Tag */}
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-orange-600 dark:text-orange-400 font-medium">
                          {article.category}
                        </span>
                        <span className="text-gray-300 dark:text-gray-600">•</span>
                        <span className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
                          <Calendar className="w-3 h-3" />
                          {new Date(article.created_at).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })}
                        </span>
                      </div>

                      {/* Article Title */}
                      <h2 className="text-3xl md:text-4xl font-light text-gray-900 dark:text-white group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors duration-300 leading-tight">
                        {article.title}
                      </h2>

                      {/* Article Excerpt */}
                      <p className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed max-w-3xl">
                        {article.excerpt}
                      </p>

                      {/* Subtle Hover Indicator */}
                      <div className="flex items-center gap-2 text-sm text-gray-400 dark:text-gray-500 group-hover:text-orange-500 dark:group-hover:text-orange-400 transition-colors duration-300">
                        <span>Read more</span>
                        <motion.div
                          className="w-0 h-px bg-current group-hover:w-8 transition-all duration-300"
                          initial={{ width: 0 }}
                          whileHover={{ width: 32 }}
                        />
                      </div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>

            {/* Enhanced Empty State */}
            {filteredArticles.length === 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-24"
              >
                <div className="text-6xl mb-8">✨</div>
                <h3 className="text-2xl font-light text-gray-900 dark:text-white mb-4">
                  Ideas in Progress
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-8 max-w-md mx-auto leading-relaxed">
                  I'm currently developing thoughts and insights to share. Check back soon for new perspectives.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link
                    to="/#contact"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors duration-200"
                  >
                    <span>Get In Touch</span>
                  </Link>
                  <Link
                    to="/projects"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-white/50 dark:bg-gray-800/50 hover:bg-white/70 dark:hover:bg-gray-800/70 text-gray-900 dark:text-white border border-gray-200/50 dark:border-gray-700/50 rounded-lg transition-colors duration-200"
                  >
                    <span>View My Projects</span>
                  </Link>
                </div>
              </motion.div>
            )}
          </div>
        </motion.section>
      </div>

    </div>
  );
}